import type { CookieOptions } from '@supabase/ssr'

type AuthGetUserResult = {
  data: {
    user: unknown | null
  }
  error: unknown | null
}

type AuthClientLike = {
  auth: {
    getUser: (jwt?: string) => Promise<AuthGetUserResult>
  }
}

interface AuthSessionRecoveryOptions {
  transientRetryDelaysMs?: readonly number[]
}

export interface ServerCookieAdapter {
  getAll(): Array<{ name: string; value: string }>
  setAll(cookiesToSet: Array<{ name: string; value: string; options: CookieOptions }>): void
}

function isSupabaseAuthCookieName(name: string): boolean {
  return name.startsWith('sb-') && name.includes('-auth-token')
}

function getAuthErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const raw = 'code' in error ? error.code : undefined
  return typeof raw === 'string' ? raw : undefined
}

function getAuthErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const raw = 'status' in error ? error.status : undefined
  return typeof raw === 'number' ? raw : undefined
}

function getAuthErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const raw = 'message' in error ? error.message : undefined
  return typeof raw === 'string' ? raw : undefined
}

function getAuthErrorName(error: unknown): string | undefined {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const raw = 'name' in error ? error.name : undefined
  return typeof raw === 'string' ? raw : undefined
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function isExpectedUnauthenticatedAuthError(error: unknown): boolean {
  if (getAuthErrorName(error) === 'AuthSessionMissingError') {
    return true
  }

  const message = getAuthErrorMessage(error)?.toLowerCase()
  if (!message) {
    return false
  }

  return message.includes('auth session missing')
}

export function isRecoverableAuthSessionError(error: unknown): boolean {
  const code = getAuthErrorCode(error)
  if (code === 'refresh_token_not_found') {
    return true
  }

  const message = getAuthErrorMessage(error)?.toLowerCase()
  if (!message) {
    return false
  }

  return (
    message.includes('user from sub claim in jwt does not exist')
    || message.includes('session from session_id claim in jwt does not exist')
  )
}

export function isTransientAuthServiceError(error: unknown): boolean {
  if (getAuthErrorName(error) === 'AuthRetryableFetchError') {
    return true
  }

  const code = getAuthErrorCode(error)
  if (code === 'unexpected_failure' || code === 'unexpected_error') {
    return true
  }

  const status = getAuthErrorStatus(error)
  if (status !== undefined && status >= 500 && status < 600) {
    return true
  }

  const message = getAuthErrorMessage(error)?.toLowerCase()
  if (!message) {
    return false
  }

  return (
    message.includes('unexpected failure')
    || message.includes('database error checking email')
    || message.includes('database error loading user')
    || message.includes('database error querying schema')
    || message.includes('database error finding user')
    || message.includes('failed to fetch')
    || message.includes('network request failed')
  )
}

async function getUserWithTransientAuthRetry(
  getUser: (jwt?: string) => Promise<AuthGetUserResult>,
  jwt: string | undefined,
  retryDelaysMs: readonly number[],
): Promise<AuthGetUserResult> {
  for (let attempt = 0; attempt <= retryDelaysMs.length; attempt += 1) {
    try {
      const result = await getUser(jwt)

      if (!isTransientAuthServiceError(result.error) || attempt === retryDelaysMs.length) {
        return result
      }
    } catch (error) {
      if (!isTransientAuthServiceError(error) || attempt === retryDelaysMs.length) {
        throw error
      }
    }

    await wait(retryDelaysMs[attempt]!)
  }

  return getUser(jwt)
}

function clearSupabaseAuthCookies(cookieAdapter: ServerCookieAdapter): void {
  const authCookies = cookieAdapter.getAll().filter(({ name }) => isSupabaseAuthCookieName(name))

  if (authCookies.length === 0) {
    return
  }

  cookieAdapter.setAll(
    authCookies.map(({ name }) => ({
      name,
      value: '',
      options: {
        path: '/',
        maxAge: 0,
        expires: new Date(0),
      },
    })),
  )
}

export function attachAuthSessionRecovery<TClient extends AuthClientLike>(
  client: TClient,
  cookieAdapter: ServerCookieAdapter,
  options: AuthSessionRecoveryOptions = {},
): TClient {
  const originalGetUser = client.auth.getUser.bind(client.auth)
  const transientRetryDelaysMs = options.transientRetryDelaysMs ?? [50, 100, 250, 500, 1_000]

  client.auth.getUser = async (jwt?: string) => {
    try {
      const result = await getUserWithTransientAuthRetry(originalGetUser, jwt, transientRetryDelaysMs)

      if (isRecoverableAuthSessionError(result.error)) {
        clearSupabaseAuthCookies(cookieAdapter)
        return {
          data: { user: null },
          error: null,
        }
      }

      return result
    } catch (error) {
      if (!isRecoverableAuthSessionError(error)) {
        throw error
      }

      clearSupabaseAuthCookies(cookieAdapter)
      return {
        data: { user: null },
        error: null,
      }
    }
  }

  return client
}
